export type SubscriptionStatus = 'active' | 'inactive';
export type CreditLineStatus = 'active' | 'inactive';
export type AddOnPurchaseStatus = 'active' | 'cancelled' | 'consumed';

export interface PlanPurchase {
  id: number;
  messageQty: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreditLine {
  id: number;
  status: CreditLineStatus;
  messageQty: number;
  createdAt: string;
  updatedAt: string;
}

export interface AddOnPurchase {
  id: number;
  status: AddOnPurchaseStatus;
  messageQty: number;
  usage: number;
  createdAt: string;
  updatedAt: string;
}

export interface Subscription {
  id: number;
  companyId: string;
  defaultRenewalDate: string;
  lastRenewalDate: string;
  nextRenewalDate: string;
  status: SubscriptionStatus;
  planPurchases?: PlanPurchase[];
  activePlan?: PlanPurchase;
  creditLine?: CreditLine;
  addOnPurchases?: AddOnPurchase[];
  createdAt: string;
  updatedAt: string;
}

export interface Company {
  companyId: string;
  name: string;
  subscription?: Subscription;
  /** Channels mapped from the backend `websites[]` (see `get-company.ts`). */
  channels: Channel[];
}

export type ChannelProvider = 'gupshup' | 'meta_cloud';
export type HsmLineStatus = 'active' | 'inactive';

/**
 * Communication channel of a company (historically called "Website" — the
 * Mongo collection and hsm-backend's contract still call it "website"; the
 * UI and the rest of this app call it "Channel" / "Canal"). The rename lives
 * in this app's boundary (`get-company.ts` maps `website → channel`).
 *
 * `provider` is the channel **capability** derived from the Kori channel type
 * (WHATSAPP_API → gupshup, WHATSAPP_BUSINESS → meta_cloud, else → null = "Sin
 * proveedor HSM"). It drives the alta modal mode and the avatar, and is known
 * even before a line exists. `channelType` is the raw Kori `type` surfaced
 * verbatim for the UI chip and the WhatsApp-only filter. `whatsappNumber` is
 * the channel's WhatsApp number with no `phone` fallback, used to flag channels
 * that cannot be onboarded ("Sin whatsappNumber"). `hsmLineStatus` is the line
 * state.
 */
export interface Channel {
  channelId: string;
  name: string;
  phone?: string;
  channelType?: string;
  whatsappNumber?: string;
  provider?: ChannelProvider | null;
  hsmLineStatus?: HsmLineStatus | null;
}

/**
 * Raw channel shape as returned by hsm-backend `GET /company/:id` inside
 * `websites[]`. Only the fields this app reads are declared; the adapter in
 * `get-company.ts` maps it to {@link Channel}.
 */
export interface BackendWebsite {
  id: string;
  title: string;
  type?: string;
  phone?: string;
  whatsappNumber?: string;
  hsmLineStatus?: HsmLineStatus | null;
}

/** Company envelope as returned by the backend, before website→channel mapping. */
export interface BackendCompany {
  companyId: string;
  name: string;
  subscription?: Subscription;
  websites?: BackendWebsite[];
}

export interface IControllerResponse<T> {
  statusCode: number;
  data: string;
  // Backend error envelope: `code` carries the error enum (e.g.
  // 'LEGACY_SUBSCRIPTION_ACTIVE') and `description` the message. `errorCode`/
  // `message` are kept for compatibility with existing call sites.
  code?: string;
  description?: string;
  errorCode?: string;
  message?: string;
  result?: T;
}

/**
 * Error `code` values from the hsm-backend `IControllerResponse` envelope that
 * the UI branches on. Single source of truth shared by the call site and its
 * test so the literal cannot drift. Mirrors `CustomErrorCode` in hsm-backend.
 */
export enum BackendErrorCode {
  LEGACY_SUBSCRIPTION_ACTIVE = 'LEGACY_SUBSCRIPTION_ACTIVE',
}
